/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import { defineMessages } from 'react-intl';

import { formatErrorResponse } from 'responseFormatterFunctions';
import { I18NError } from 'Utils';
import { fetchGet } from 'FetchAPIs';

const baseHierarchiesURL = '/api/hierarchies';

const messages = defineMessages({
  errorLoadingSystemForSite: 'Error loading system. {message}',
  errorLoadingLocationsForSystem: 'Error loading locations. {message}',
  errorLoadingSublocationsForSystem: 'Error loading sublocations. {message}',
});

/**
 * Function that loads system info by id for a specific site/system API
 * @param siteId - site id
 * @param systemId - system id
 * @returns existing system for the specified site
 */
export const getSystemBySiteId = async (siteId: string, systemId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/systems/${systemId}`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSystemForSite, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads locations for a specific site/system API
 * @param siteId - site id
 * @param systemId - system id
 * @returns existing locations for the specified system/system
 */
export const getLocationsForSystem = async (siteId: string, systemId: string): Promise<any> =>
  fetchGet(`${baseHierarchiesURL}/sites/${siteId}/systems/${systemId}/locations`, null, null)
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingLocationsForSystem, {
        message: formatErrorResponse(error),
      });
    });

/**
 * Function that loads sublocations for a specific site/system/location API
 * @param siteId - site id
 * @param systemId - system id
 * @param locationId - location id
 * @returns existing sublocations for the specified system/system/locations
 */
export const getSublocationsForSystem = async (
  siteId: string,
  systemId: string,
  locationId: string,
  page: number,
  limit: number
): Promise<any> => {
  const queryParams = {
    page,
    limit,
  };

  return fetchGet(
    `${baseHierarchiesURL}/sites/${siteId}/systems/${systemId}/locations/${locationId}/sublocations`,
    null,
    queryParams
  )
    .then(response => response)
    .catch(error => {
      throw new I18NError(messages.errorLoadingSublocationsForSystem, {
        message: formatErrorResponse(error),
      });
    });
};
